import { useState } from "react";
import { useSearchParams } from "react-router";

import FormFieldViewText from "components/FormFields/ViewText";
import { ErrorCircleColored } from "components/icons/generated";
import SpaceliftLoader from "components/loading/SpaceliftLoader";
import SystemMessage from "components/SystemMessage";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import useTitle from "hooks/useTitle";
import setLocation from "shared/setLocation";
import { FetchError } from "utils/errors";
import { getBaseUrl } from "utils/getBaseUrl";
import { extractHost } from "utils/urls";

import getFlowsLinkConfirmErrorMessage from "./getErrorMessage";
import useFlowsLinkConfirmInfo from "./useInfo";

const FlowsLinkConfirm = () => {
  useTitle("Enable Flows");

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const { info, isLoading, loadError } = useFlowsLinkConfirmInfo(token);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      const response = await fetch("/flows/link/complete", {
        method: "POST",
        body: new URLSearchParams({ token }),
      });

      if (!response.ok) {
        throw new FetchError(response.status, (await response.text()).trim());
      }

      setLocation("/flows/login");
    } catch (error) {
      setSubmitError(getFlowsLinkConfirmErrorMessage(error));
      // Keep loading until navigation on success; only re-enable the button on failure.
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return <SystemMessage icon={ErrorCircleColored} title="Linking failed" message={loadError} />;
  }

  if (isLoading || !info) {
    return <SpaceliftLoader />;
  }

  const accountName = extractHost(getBaseUrl()).split(".")[0];

  return (
    <SystemMessage title="Enable Flows" message="Finish enabling Flows for this Spacelift account.">
      <Box direction="column" gap="xx-large" fullWidth>
        <Box direction="column" gap="x-large" fullWidth>
          <FormFieldViewText
            label="Spacelift account"
            tooltipInfo="The Spacelift account that will be linked to the Flows organization."
            tooltipWidthMode="maxWidthSm"
            value={accountName}
            noMargin
          />
          <FormFieldViewText
            label="Flows organization"
            tooltipInfo="The Flows organization that will be linked to this Spacelift account."
            tooltipWidthMode="maxWidthSm"
            value={info.flowsOrganizationName}
            noMargin
          />
        </Box>

        {submitError && (
          <Banner variant="danger" fullWidth>
            {submitError}
          </Banner>
        )}

        <Button variant="primary" onPress={handleConfirm} loading={isSubmitting} fullWidth>
          {submitError ? "Try again" : "Confirm"}
        </Button>
      </Box>
    </SystemMessage>
  );
};

export default FlowsLinkConfirm;
